<template>
	<div class="navigator-header p-mb-3">
		<p class="title p-my-2">{{ $t("Squeeze.Reports.FieldSelectionQuality") }}</p>
	</div>
	<div>
		<div class="p-fluid p-component p-formgrid p-grid p-input-filled">
			<div class="p-field p-col-12 p-sm-6 p-lg-6 p-xl-3">
				<Calendar
					id="range"
					v-model="selectedDate"
					selectionMode="range"
					:maxDate="maxDate"
					:manualInput="false"
					:showIcon="true"
					:placeholder="datePlaceholder"
				>
					<template #value="slotProps">
						<div class="date-item date-item-value" v-if="slotProps.selectedDate">
							<div>{{ slotProps.selectedDate }}</div>
						</div>
						<span v-else>
                            {{ slotProps.placeholder }}
                        </span>
					</template>
				</Calendar>
			</div>
			<div class="p-field p-col-12 p-sm-6 p-lg-6 p-xl-3">
				<MultiSelect
					v-model="selectedDocumentClasses"
					:options="allDocumentClasses"
					optionValue="id"
					optionLabel="name"
					:filter="true"
					:autoFilterFocus="true"
					@show="showDropdownOverlay"
				>
					<template #value="slotProps">
						<div v-if="slotProps.value.length" v-tooltip.bottom="slotProps.value ? mapTooltipDcValues(slotProps.value) : ''">
							{{ getOptionDcLabel(slotProps.value) }}
						</div>
						<div v-else class="p-placeholder" data-pc-section="label">{{ $t('Squeeze.Reports.SelectDocumentClasses') }}</div>
					</template>
					<template #option="slotProps">
						{{ slotProps.option.name }}
					</template>
				</MultiSelect>
			</div>
			<div class="p-field p-col-12 p-sm-6 p-lg-6 p-xl-2">
				<MultiSelect
					v-model="selectedFieldNames"
					:options="allFields.filter(field => field.useInReport)"
					optionValue="name"
					optionLabel="name"
					:filter="true"
					:autoFilterFocus="true"
					@show="showDropdownOverlay"
				>
					<template #value="slotProps">
						<div v-if="slotProps.value.length" v-tooltip.bottom="slotProps.value ? slotProps.value.map(value => value).join(', ') : ''">
							{{ getOptionFieldLabel(slotProps.value) }}
						</div>
						<div v-else class="p-placeholder" data-pc-section="label">{{ $t('Squeeze.Reports.SelectFields') }}</div>
					</template>
					<template #option="slotProps">
						{{ slotProps.option.name }}
					</template>
				</MultiSelect>
			</div>
			<div class="p-field p-col-12 p-sm-6 p-lg-6 p-xl-2">
				<IconField>
					<InputIcon class="mdi mdi-comment-question-outline" v-tooltip.top="$t('Squeeze.Reports.CompanyHelpText')" style="cursor: pointer;" />
					<InputText
						v-model="companyIds"
						id="companyIds"
						type="text"
						:placeholder="$t('Squeeze.Reports.Company')"
					/>
				</IconField>
			</div>
			<div class="p-field p-col-12 p-sm-6 p-lg-6 p-xl-2">
				<IconField>
					<InputIcon class="mdi mdi-comment-question-outline" v-tooltip.top="$t('Squeeze.Reports.CreditorHelpText')" style="cursor: pointer;" />
					<InputText
						v-model="creditorIds"
						id="creditorIds"
						type="text"
						:placeholder="$t('Squeeze.Reports.Creditor')"
					/>
				</IconField>
			</div>
			<ButtonGroup class="p-field p-col-12 p-sm-6 p-lg-6 p-xl-2 p-ml-auto" style="justify-content: flex-end;">
				<Button
					style="flex: 0; padding: 0.714rem 1.714rem;"
					icon="mdi mdi-filter-off-outline"
					v-tooltip.top="$t('Squeeze.General.ClearFilters')"
					@click="clearFilters"
				/>
				<Button
					icon="mdi mdi-filter-outline"
					:label="$t('Squeeze.Reports.Filter')"
					@click="filter"
				/>
			</ButtonGroup>
		</div>

		<DataTable
			:value="reportData"
			:loading="loading"
			style="min-height: calc(100vh - 40rem); max-height: calc(100vh - 25rem);"
			class="p-datatable-sm report-table"
			columnResizeMode="fit"
			:autoLayout="true"
			showGridlines
			scrollable
			scrollHeight="flex"
			:paginator="true"
			:rows="tableListPagination.pageSize"
			paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
			:rowsPerPageOptions="[25,50,100]"
			:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords}  '"
			@page="onChangePage"
		>
			<template #loading>
				{{  $t('Squeeze.General.Loading') }}
			</template>
			<template #empty>
				{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Reports.Data')}) }}
			</template>
			<ColumnGroup type="header">
				<Row>
					<Column :header="$t('Squeeze.Reports.Table.SelectedFields')" :rowspan="3" />
					<Column :header="$t('Squeeze.Reports.Table.FieldProcessedCorrectly')" :colspan="2" />
					<Column :header="$t('Squeeze.Reports.Table.FieldEmpty')" :colspan="3" />
					<Column :header="$t('Squeeze.Reports.Table.FieldInvalid')" :colspan="3" />
					<Column :header="$t('Squeeze.Reports.Table.Total')" :rowspan="3" />
				</Row>
				<Row>
					<Column :header="$t('Squeeze.Reports.Table.Value')" field="valid" />
					<Column :header="$t('Squeeze.Reports.Table.InPercent')" field="validPercentage" />
					<Column :header="$t('Squeeze.Reports.Table.Value')" field="empty" />
					<Column :header="$t('Squeeze.Reports.Table.InPercent')" field="emptyPercentage" />
					<Column :header="$t('Squeeze.Reports.Table.Alternatives')" field="emptyAlternatives" />
					<Column :header="$t('Squeeze.Reports.Table.Value')" field="invalid" />
					<Column :header="$t('Squeeze.Reports.Table.InPercent')" field="validPercentage" />
					<Column :header="$t('Squeeze.Reports.Table.Alternatives')" field="invalidAlternatives" />
				</Row>
			</ColumnGroup>
			<Column field="name" :header="$t('Squeeze.Reports.Table.SelectedFields')" style="min-width: 10rem;" />
			<Column field="valid" :header="$t('Squeeze.Reports.Table.FieldProcessedCorrectly')" style="min-width: 5rem;" />
			<Column field="validPercentage" :header="$t('Squeeze.Reports.Table.InPercent')" style="min-width: 5rem;">
				<template #body="slotProps">
					{{ formatValue(slotProps.data.validPercentage) }}%
				</template>
			</Column>
			<Column field="empty" :header="$t('Squeeze.Reports.Table.FieldEmpty')" style="min-width: 5rem;" />
			<Column field="emptyPercentage" :header="$t('Squeeze.Reports.Table.InPercent')" style="min-width: 5rem;">
				<template #body="slotProps">
					{{ formatValue(slotProps.data.emptyPercentage) }}%
				</template>
			</Column>
			<Column field="emptyAlternatives" :header="$t('Squeeze.Reports.Table.Alternatives')" style="min-width: 10rem;" />
			<Column field="invalid" :header="$t('Squeeze.Reports.Table.FieldInvalid')" style="min-width: 5rem;" />
			<Column field="invalidPercentage" :header="$t('Squeeze.Reports.Table.InPercent')" style="min-width: 5rem;">
				<template #body="slotProps">
					{{ formatValue(slotProps.data.invalidPercentage) }}%
				</template>
			</Column>
			<Column field="invalidAlternatives" :header="$t('Squeeze.Reports.Table.Alternatives')" style="min-width: 10rem;" />
			<Column field="processed" :header="$t('Squeeze.Reports.Table.Total')" style="min-width: 5rem;" />

			<template #paginatorstart></template>
			<template #paginatorend></template>
		</DataTable>
	</div>
</template>

<script lang="ts">
import {defineComponent, onBeforeMount, onMounted, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {ClientManager} from "@/singletons/ClientManager";
import ButtonGroup from 'primevue/buttongroup';
import MultiSelect from 'primevue/multiselect';
import Calendar from "primevue/calendar";
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import moment from "moment";
import {DetailedReport, DocumentClass, DocumentField} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import {showDropdownOverlay} from "@/util/StylesHelper";
import {formatDateRangePlaceholder} from "@/util/DateHelper";
import {TableListSettings, TableSettings} from "@/util/TableSettings";
import {useRoute} from "vue-router";
import useSqueezeStore from "@/apps/squeeze/store";
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';

export default defineComponent({
	name: "DocumentReportFieldSelectionQuality",
	methods: {showDropdownOverlay},
	components: {
		IconField,
		InputIcon,
		InputText,
		DataTable,
		Column,
		Row,
		ColumnGroup,
		Calendar,
		MultiSelect,
		ButtonGroup,
	},
	setup() {
		const {t, locale} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** Show loading in table? */
		const loading = ref<boolean>(false);

		/** List of all detailed Reports */
		const reportData = ref<DetailedReport[]>([]);

		/** List of all DocumentClasses */
		const allDocumentClasses = ref<DocumentClass[]>([]);

		/** List of all Fields */
		const allFields = ref<DocumentField[]>([]);

		/** Currently selected Document-Classes by ID */
		const selectedDocumentClasses = ref<number[]>([]);

		/** Currently selected Document class fields by ID */
		const selectedFieldNames = ref<string[]>([]);

		/** Filter for creditor by creditor number */
		const creditorIds = ref<string>('');

		/** Filter for company by company number */
		const companyIds = ref<string>('');

		/** Selected date */
		const selectedDate = ref<string[]>([]);

		/** Date placeholder */
		const datePlaceholder = ref<string>('');

		/** Max Date (current Date) */
		const maxDate = ref(new Date());

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/** Service for User API */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Service for Document Class API */
		const documentClassApi = ClientManager.getInstance().squeeze.documentClass;

		/** Service for report API */
		const reportApi = ClientManager.getInstance().squeeze.report;

		const getTableData = async (startDate: moment.Moment, endDate: moment.Moment) => {
			try {
				loading.value = true;
				datePlaceholder.value = formatDateRangePlaceholder(startDate, endDate, locale.value);
				reportData.value = await reportApi.getDetailedExtractionReport(
					selectedDocumentClasses.value.toString(),
					creditorIds.value,
					companyIds.value,
					selectedFieldNames.value.toString(),
					startDate.format(),
					endDate.format()
				);
			} catch (err: any) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), err);
			} finally {
				loading.value = false;
			}
		}

		/** Clear filters */
		const clearFilters = () => {
			selectedDate.value = [];
			selectedDocumentClasses.value = [];
			selectedFieldNames.value = [];
			creditorIds.value = '';
			companyIds.value = '';
			getTableData(moment('01.01.', 'DD MM'), moment());
		}

		/** Triggered to filter */
		const filter = () => {
			if (selectedDate.value[1]) {
				getTableData(moment(selectedDate.value[0]), moment(selectedDate.value[1]).endOf('day'));
			} else {
				// when endDate not set, then set endDate to startDate
				getTableData(moment(selectedDate.value[0]), moment(selectedDate.value[0]).endOf('day'));
			}
		}

		/** Get all DocumentClasses */
		const getAllDocumentClasses = () => {
			documentClassApi.getAllDocumentClasses()
				.then((documentClasses) => {
					allDocumentClasses.value = documentClasses;
				})
				.catch((error: {message: string}) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), error.message);
				})
		}

		/** Get all Fields */
		const getAllFields = () => {
			documentClassApi.getAllFields()
				.then((fields) => {
					allFields.value = fields;
				})
				.catch((error: {message: string}) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), error.message);
				})
		}

		/**
		 * Triggered when the tooltip is shown
		 * for document class
		 * @param values
		 */
		const mapTooltipDcValues = (values: string[]) => {
			return values
				.map((value: string) => {
					return allDocumentClasses.value.find(docClass => docClass.id === Number(value))?.name;
				})
				.join(", ");
		}

		/**
		 * Get option label of selected document class
		 * @param options
		 */
		const getOptionDcLabel = (options: number[]) => {
			let label = '';
			options.forEach((option: any, index: number) => {
				if (index <= 2) {
					const currentDocClass = allDocumentClasses.value.find(docClass => docClass.id === option);
					if (currentDocClass) {
						label += currentDocClass.name;
						if (options.length > 1 && index < options.length - 1) {
							label += ', ';
						}
					}
				} else if (index === 3) {
					label += '...';
				}
			})

			return label;
		}

		/**
		 * Get option label of selected fields
 		 * @param options
		 */
		const getOptionFieldLabel = (options: string[]) => {
			let label = '';
			options.forEach((option: any, index: number) => {
				if (index <= 2) {
					label += option;
					if (options.length > 1 && index < options.length - 1) {
						label += ', ';
					}
				} else if (index === 3) {
					label += '...';
				}
			})

			return label;
		}

		/**
		 * Format value to three decimal places if one decimal place is available
		 * @param value
		 */
		const formatValue = (value: number) => {
			return Number.isSafeInteger(value) ? value : value.toFixed(3);
		}

		/**
		 * Triggered when page is changed
		 * @param event
		 */
		const onChangePage = (event: { page: number; rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		onMounted(() => {
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
			getAllDocumentClasses();
			getAllFields();
		})

		onBeforeMount(async () => {
			await getTableData(moment('01.01.', 'DD MM'), moment());
		})

		return {
			t,
			locale,
			toast,
			route,
			store,
			loading,
			reportData,
			allDocumentClasses,
			allFields,
			selectedDocumentClasses,
			selectedFieldNames,
			creditorIds,
			companyIds,
			selectedDate,
			datePlaceholder,
			maxDate,
			tableListPagination,
			clearFilters,
			filter,
			mapTooltipDcValues,
			getOptionDcLabel,
			getOptionFieldLabel,
			formatValue,
			onChangePage,
		};
	},
});

</script>

<style lang="scss" scoped>

.navigator-header {
	color: var(--dex-primary-color);
}

.title {
	font-size: 2rem;
}

// is required so that the table header keeps the right size
.report-table {
	& :deep(table) {
		display: table !important;

		thead {
			display: table-header-group;

			tr {
				display: table-row;

				th {
					display: table-cell;
				}
			}
		}
		tbody {
			display: table-header-group;

			tr {
				display: table-row;

				td {
					display: table-cell;
				}
			}
		}
	}
}

::v-deep(.p-datatable.p-datatable-gridlines .p-datatable-thead tr th) {
	border-width: 0px 1px 0px 1px;
}

::v-deep(.p-datatable.p-datatable-gridlines .p-paginator-bottom) {
    border: none;
}

::v-deep(.p-multiselect .p-multiselect-label) {
	padding: 0.7rem 0.7rem;
}

::v-deep(.p-multiselect-label-empty) {
	overflow: unset;
	visibility: unset;
}

::v-deep(.p-calendar-w-btn) {
	border: none !important;
}

</style>
